
import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2,
  CalendarDays,
  CreditCard,
  BriefcaseBusiness,
  Send,
  Download,
  Eye,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

import PartnerHeader from "../../component/PartnerHeader";
import PartnerFooter from "../../component/PartnerFooter";

import {
  getActiveSubscription,
  getActiveFeatureUsage,
} from "../../services/subscriptionProductService";

import "./SubscriptionPurchases.css";

const getCurrentUser = () => {
  try {
    const userRaw = localStorage.getItem("user");
    const user = userRaw ? JSON.parse(userRaw) : {};

    const directId =
      localStorage.getItem("userId") ||
      localStorage.getItem("user_id");

    const id =
      directId || user?.id || user?.userId || user?.user_id || 0;

    let userType =
      user?.userType ||
      user?.user_type ||
      user?.role ||
      localStorage.getItem("userType") ||
      localStorage.getItem("user_type") ||
      localStorage.getItem("role") ||
      "";

    userType = String(userType).toUpperCase();

    if (userType === "RECRUITER") {
      userType = "PARTNER";
    }

    return {
      id: Number(String(id).replace(/"/g, "")) || 0,
      userType,
    };
  } catch (error) {
    console.error("Unable to get current user:", error);
    return { id: 0, userType: "" };
  }
};

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLimit = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (number < 0) return "Unlimited";

  return number.toLocaleString("en-IN");
};

const getRemaining = (limit, used, remainingCount) => {
  if (remainingCount !== undefined && remainingCount !== null) {
    return Number(remainingCount) < 0
      ? "Unlimited"
      : Math.max(0, Number(remainingCount)).toLocaleString("en-IN");
  }

  const limitValue = Number(limit ?? 0);
  const usedValue = Number(used ?? 0);

  if (limitValue < 0) return "Unlimited";

  return Math.max(0, limitValue - usedValue).toLocaleString("en-IN");
};

const getPercentage = (limit, used) => {
  const limitValue = Number(limit ?? 0);
  const usedValue = Number(used ?? 0);

  if (limitValue < 0) return 100;
  if (limitValue <= 0) return 0;

  return Math.min(100, Math.max(0, (usedValue / limitValue) * 100));
};

const UsageCard = ({
  icon: Icon,
  title,
  used,
  limit,
  remainingCount,
  unavailable = false,
}) => {
  const percentage = getPercentage(limit, used);
  const unlimited = Number(limit ?? 0) < 0;

  return (
    <div className="my-sub-usage-card">
      <div className="my-sub-usage-header">
        <div className="my-sub-usage-icon">
          <Icon size={20} />
        </div>

        <div>
          <h4>{title}</h4>
          <p>
            {unavailable
              ? "Usage unavailable"
              : unlimited
                ? `${Number(used || 0)} used`
                : `${Number(used || 0)} / ${Number(limit || 0)} used`}
          </p>
        </div>
      </div>

      <div className="my-sub-progress">
        <div
          className="my-sub-progress-bar"
          style={{
            width: unavailable ? "0%" : `${percentage}%`,
          }}
        />
      </div>

      <div className="my-sub-usage-footer">
        <span>
          Used:{" "}
          <strong>{unavailable ? "-" : Number(used || 0)}</strong>
        </span>

        <span>
          Remaining:{" "}
          <strong>
            {unavailable ? "-" : getRemaining(limit, used, remainingCount)}
          </strong>
        </span>
      </div>

      {unavailable && (
        <p className="my-sub-usage-error">
          Unable to fetch current usage. Please refresh.
        </p>
      )}
    </div>
  );
};

export default function MySubscription() {
  const [subscription, setSubscription] = useState(null);

  const [jobPostUsage, setJobPostUsage] = useState(null);
  const [resumeUsage, setResumeUsage] = useState(null);
  const [jobInviteUsage, setJobInviteUsage] = useState(null);
  const [profileViewUsage, setProfileViewUsage] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [usageError, setUsageError] = useState("");

  const loadSubscription = useCallback(async (showRefresh = false) => {
    const currentUser = getCurrentUser();

    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");
      setUsageError("");

      if (!currentUser.id) {
        throw new Error("Unable to identify the logged-in user.");
      }

      const subscriptionData = await getActiveSubscription(
        currentUser.id
      );

      setSubscription(subscriptionData || null);

      // Clear previous usage before loading fresh data.
      setJobPostUsage(null);
      setResumeUsage(null);
      setJobInviteUsage(null);
      setProfileViewUsage(null);

      if (!subscriptionData) return;

      let userType =
        currentUser.userType ||
        String(subscriptionData.userType || "").toUpperCase();

      if (userType === "RECRUITER") {
        userType = "PARTNER";
      }

      if (!userType) {
        setUsageError("Unable to identify your account type.");
        return;
      }

      const features = [
        ["JOB_POST", setJobPostUsage],
        ["RESUME_DOWNLOAD", setResumeUsage],
        ["JOB_INVITE", setJobInviteUsage],
        ["PROFILE_VIEW", setProfileViewUsage],
      ];

      const results = await Promise.all(
        features.map(async ([featureCode, setUsage]) => {
          try {
            const usageData = await getActiveFeatureUsage(
              currentUser.id,
              userType,
              featureCode
            );

            setUsage(usageData || null);

            return {
              featureCode,
              success: Boolean(usageData),
            };
          } catch (usageErr) {
            console.error(
              `Unable to load ${featureCode} usage:`,
              usageErr
            );

            setUsage(null);

            return {
              featureCode,
              success: false,
            };
          }
        })
      );

      if (results.some((result) => !result.success)) {
        setUsageError(
          "Some usage details could not be loaded. Please refresh."
        );
      }
    } catch (err) {
      console.error("Unable to load active subscription:", err);

      setSubscription(null);
      setJobPostUsage(null);
      setResumeUsage(null);
      setJobInviteUsage(null);
      setProfileViewUsage(null);

      setError(
        err?.message || "Unable to load your active subscription."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  if (loading) {
    return (
      <>
        <PartnerHeader />
        <div className="my-sub-page">
          <div className="my-sub-loading">
            <div className="my-sub-spinner" />
            <p>Loading your subscription...</p>
          </div>
        </div>
        <PartnerFooter />
      </>
    );
  }

  const renderLimit = (usage, fallbackLimit) =>
    formatLimit(usage?.allowedLimit ?? fallbackLimit);

  return (
    <>
      <PartnerHeader />

      <div className="my-sub-page">
        <div className="my-sub-container">
          <div className="my-sub-page-header">
            <div>
              <span className="my-sub-kicker">Subscription</span>
              <h1>My Subscription</h1>
              <p>
                View your purchased plan, validity and current usage.
              </p>
            </div>

            <button
              type="button"
              className="my-sub-refresh-btn"
              onClick={() => loadSubscription(true)}
              disabled={refreshing}
            >
              <RefreshCw
                size={17}
                className={refreshing ? "my-sub-spin" : ""}
              />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {error && (
            <div className="my-sub-error">
              <AlertCircle size={20} />
              <div>
                <strong>Unable to load subscription</strong>
                <p>{error}</p>
              </div>
              <button
                type="button"
                onClick={() => loadSubscription()}
              >
                Try Again
              </button>
            </div>
          )}

          {!error && !subscription && (
            <div className="my-sub-empty">
              <div className="my-sub-empty-icon">
                <CreditCard size={34} />
              </div>
              <h2>No Active Subscription</h2>
              <p>
                You don't currently have an active subscription plan.
              </p>
            </div>
          )}

          {subscription && (
            <>
              <div className="my-sub-plan-card">
                <div className="my-sub-plan-top">
                  <div>
                    <span className="my-sub-plan-badge">
                      ACTIVE PLAN
                    </span>
                    <h2>
                      {subscription.planName || "Subscription Plan"}
                    </h2>
                    {subscription.planCode && (
                      <p className="my-sub-plan-code">
                        {subscription.planCode}
                      </p>
                    )}
                  </div>

                  <div className="my-sub-active">
                    <CheckCircle2 size={19} />
                    Active
                  </div>
                </div>

                <div className="my-sub-plan-info">
                  <div>
                    <span>Plan Price</span>
                    <strong>{money(subscription.price)}</strong>
                  </div>
                  <div>
                    <span>Total Paid</span>
                    <strong>{money(subscription.totalAmount)}</strong>
                  </div>
                  <div>
                    <span>Purchase Date</span>
                    <strong>{formatDate(subscription.purchaseDate)}</strong>
                  </div>
                  <div>
                    <span>Valid From</span>
                    <strong>{formatDate(subscription.startDate)}</strong>
                  </div>
                  <div>
                    <span>Valid Until</span>
                    <strong>{formatDate(subscription.expiryDate)}</strong>
                  </div>
                  <div>
                    <span>Payment</span>
                    <strong>{subscription.paymentStatus || "PAID"}</strong>
                  </div>
                </div>
              </div>

              <div className="my-sub-section-heading">
                <div>
                  <h2>Your Plan Usage</h2>
                  <p>
                    Your current usage against the limits included in
                    your purchased plan.
                  </p>
                </div>
              </div>

              {usageError && (
                <div className="my-sub-error">
                  <AlertCircle size={20} />
                  <div>
                    <strong>Some usage details could not be loaded</strong>
                    <p>{usageError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadSubscription(true)}
                    disabled={refreshing}
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className="my-sub-usage-grid">
                <UsageCard
                  icon={Download}
                  title="Resume Downloads"
                  used={resumeUsage?.usedCount}
                  limit={resumeUsage?.allowedLimit}
                  remainingCount={resumeUsage?.remainingCount}
                  unavailable={!resumeUsage}
                />

                <UsageCard
                  icon={BriefcaseBusiness}
                  title="Job Posts"
                  used={jobPostUsage?.usedCount}
                  limit={jobPostUsage?.allowedLimit}
                  remainingCount={jobPostUsage?.remainingCount}
                  unavailable={!jobPostUsage}
                />

                <UsageCard
                  icon={Send}
                  title="Job Invites"
                  used={jobInviteUsage?.usedCount}
                  limit={jobInviteUsage?.allowedLimit}
                  remainingCount={jobInviteUsage?.remainingCount}
                  unavailable={!jobInviteUsage}
                />

                <UsageCard
                  icon={Eye}
                  title="Profile Views"
                  used={profileViewUsage?.usedCount}
                  limit={profileViewUsage?.allowedLimit}
                  remainingCount={profileViewUsage?.remainingCount}
                  unavailable={!profileViewUsage}
                />
              </div>

              <div className="my-sub-details-card">
                <div className="my-sub-details-header">
                  <div className="my-sub-details-icon">
                    <CalendarDays size={21} />
                  </div>
                  <div>
                    <h3>Subscription Details</h3>
                    <p>
                      Details of your current purchased subscription.
                    </p>
                  </div>
                </div>

                <div className="my-sub-details-grid">
                  <div>
                    <span>Subscription ID</span>
                    <strong>#{subscription.id ?? "-"}</strong>
                  </div>
                  <div>
                    <span>Product ID</span>
                    <strong>
                      #{subscription.subscriptionProductId ?? "-"}
                    </strong>
                  </div>
                  <div>
                    <span>User Type</span>
                    <strong>{subscription.userType || "-"}</strong>
                  </div>
                  <div>
                    <span>Payment Method</span>
                    <strong>{subscription.paymentMethod || "-"}</strong>
                  </div>
                  <div>
                    <span>Transaction ID</span>
                    <strong>{subscription.transactionId || "-"}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong className="my-sub-status">
                      {subscription.status || "-"}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="my-sub-features-card">
                <h3>Plan Includes</h3>
                <div className="my-sub-features">
                  <div>
                    <CheckCircle2 size={17} />
                    Resume Downloads:{" "}
                    <strong>
                      {renderLimit(resumeUsage, subscription.resumeLimit)}
                    </strong>
                  </div>
                  <div>
                    <CheckCircle2 size={17} />
                    Job Posts:{" "}
                    <strong>
                      {renderLimit(jobPostUsage, subscription.jobPostLimit)}
                    </strong>
                  </div>
                  <div>
                    <CheckCircle2 size={17} />
                    Job Invites:{" "}
                    <strong>
                      {renderLimit(jobInviteUsage, subscription.jobInviteLimit)}
                    </strong>
                  </div>
                  <div>
                    <CheckCircle2 size={17} />
                    Profile Views:{" "}
                    <strong>
                      {renderLimit(profileViewUsage, subscription.profileViewLimit)}
                    </strong>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <PartnerFooter />
    </>
  );
}
